import { Assert } from "@seriously-rad/seriouslyrad-framework-core/Assert";
import { DataSource } from "@seriously-rad/seriouslyrad-framework-core/Data/DataSource";
import { PartialNullable } from "@seriously-rad/seriouslyrad-framework-core/Extensions";
import { StringHelper } from "@seriously-rad/seriouslyrad-framework-core/Helpers/StringHelper";
import { IJNativeEvent } from "@seriously-rad/seriouslyrad-framework-core/JNative";
import { Property } from "@seriously-rad/seriouslyrad-framework-core/RadObject";
import { ClassPropertyDialog } from "@seriously-rad/seriouslyrad-framework-core/Ui/Controls/ClassPropertyDialog";
import { ComboBox } from "@seriously-rad/seriouslyrad-framework-core/Ui/Controls/ComboBox";
import { ComboBoxYesNo } from "@seriously-rad/seriouslyrad-framework-core/Ui/Controls/ComboBoxYesNo";
import { IInputControl, InputControl, InputControlPropertyValues } from "@seriously-rad/seriouslyrad-framework-core/Ui/Controls/InputControl";
import { Label } from "@seriously-rad/seriouslyrad-framework-core/Ui/Controls/Label";
import { IDataTypeInfo } from "./ProcessDesigner/IDataTypeInfo";
import { IProcessDesigner } from "./ProcessDesigner/IProcessDesigner";
import { NAMESPACE } from "./_Namespace";

export type RuntimeDataTypePropertyValues =
    InputControlPropertyValues
    & {
    };

export type RuntimeDataTypeProperties = {
    [name in keyof RuntimeDataTypePropertyValues]: Property<RuntimeDataTypePropertyValues[name]>
};

export interface IRuntimeDataType extends IInputControl, RuntimeDataTypeProperties
{
}

export class RuntimeDataType extends InputControl implements IRuntimeDataType
{
    // #region Static

    public static override classNamespace = NAMESPACE + "RuntimeDataType";
    public static override className = "RuntimeDataType";

    // #region Overrides

    public static override createPropertyDefaults(): PartialNullable<RuntimeDataTypePropertyValues>
    {
        return {
            ...super.createPropertyDefaults(),
            ...{
            }
        };
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Global Variables

    protected comboBoxDataType?: ComboBox;
    protected comboBoxIsEnumerable?: ComboBoxYesNo;
    protected labelIsEnumerable?: Label;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<RuntimeDataTypePropertyValues>)
    {
        super(propertyValues);
    }

    // #endregion

    // #region Public Methods

    public loadDataAsync()
    {
        const
            page = this.page.get();

        if (!page)
            return Promise.resolve();

        Assert.isInstanceOfClass(ClassPropertyDialog, page, "page");

        let
            allDataTypes: IDataTypeInfo[] = [];

        const
            instanceAsAny = page.instance as any;

        if (instanceAsAny && instanceAsAny.processDesigner?.get)
        {
            const
                processDesigner = instanceAsAny.processDesigner.get() as IProcessDesigner;

            if (processDesigner)
            {
                allDataTypes =
                    processDesigner
                        ._getAllDataTypes()
                        .map(item =>
                        {
                            return {
                                description: item.description,
                                name: item.name,
                                type: item.type,
                                typeFullName: item.typeFullName,
                                value: StringHelper.format("{0}|{1}", item.type, item.typeFullName)
                            };
                        });
            }
        }

        Assert.isNotNullOrUndefined(this.comboBoxDataType, "this.comboBoxDataType");

        this.comboBoxDataType.listDataSource.set(new DataSource({ data: allDataTypes }));
    }

    // #endregion

    // #region Protected Methods

    protected _updateValue()
    {
        Assert.isNotNullOrUndefined(this.comboBoxDataType, "this.comboBoxDataType");
        Assert.isNotNullOrUndefined(this.comboBoxIsEnumerable, "this.comboBoxIsEnumerable");

        const
            dataType = this.comboBoxDataType.selectedItem.get(),
            isEnumerable = this.comboBoxIsEnumerable.value.get();

        this.value.set({
            type: dataType ? dataType.type : null,
            fullName: dataType ? dataType.typeFullName : null,
            isEnumerable: !!isEnumerable
        });
    }

    // #endregion

    // #region Overrides

    protected override _bindEvents()
    {
        super._bindEvents();

        Assert.isNotNullOrUndefined(this.comboBoxDataType, "this.comboBoxDataType");
        Assert.isNotNullOrUndefined(this.comboBoxIsEnumerable, "this.comboBoxIsEnumerable");

        this.comboBoxDataType.on("valueChanged", { self: this }, this.onComboBoxDataTypeValueChanged);
        this.comboBoxIsEnumerable.on("valueChanged", { self: this }, this.onComboBoxIsEnumerableValueChanged);
    }
    protected override _createElement()
    {
        const
            $$element = super._createElement();

        this.comboBoxDataType =
            new ComboBox()
                .appendTo($$element);

        this.labelIsEnumerable =
            new Label({ text: "Is List?" })
                .appendTo($$element);

        this.comboBoxIsEnumerable =
            new ComboBoxYesNo()
                .appendTo($$element);

        return $$element;
    }
    protected override _createElementComplete()
    {
        super._createElementComplete();

        this.addCssClass("rad-control-runtime-data-type field");
    }
    protected override _refreshFromDataSource()
    {
        super._refreshFromDataSource();

        const
            dataSource = this.dataSource.get(),
            dataSourceProperty = this.dataSourceProperty.get();

        if (!dataSource || !dataSourceProperty)
            return;

        const
            dataSourceValue = dataSource.propertyValueByPathGet(dataSourceProperty);

        Assert.isNotNullOrUndefined(this.comboBoxDataType, "this.comboBoxDataType");
        Assert.isNotNullOrUndefined(this.comboBoxIsEnumerable, "this.comboBoxIsEnumerable");

        this.comboBoxDataType.value.set(dataSourceValue ? StringHelper.format("{0}|{1}", dataSourceValue.type, dataSourceValue.fullName) : null);
        this.comboBoxIsEnumerable.value.set(dataSourceValue && dataSourceValue.isEnumerable ? dataSourceValue.isEnumerable : false);
    }

    // #endregion

    // #region Event Handlers

    protected onComboBoxDataTypeValueChanged(evt: IJNativeEvent)
    {
        const
            self = evt.data.self as this;

        if (self.__refreshingFromDataSource)
            return;

        self._updateValue();
    }
    protected onComboBoxIsEnumerableValueChanged(evt: IJNativeEvent)
    {
        const
            self = evt.data.self as this;

        if (self.__refreshingFromDataSource)
            return;

        self._updateValue();
    }

    // #endregion

    // #endregion
}

